// Cookie consent -GDPR / ePrivacy compliance.
//
// Two states a visitor can choose:
//   - "accepted" → all cookies allowed (essential + analytics + future tracking)
//   - "essential" → only strictly necessary (auth, locale, consent itself)
//
// Versioning: bumping CONSENT_VERSION invalidates all stored choices, so when
// you add a new tracking category you can re-prompt cleanly.
//
// The consent cookie itself is essential -it's the only way to remember the
// user's choice -so it's set without prior consent, which is allowed under
// the ePrivacy "strictly necessary" carve-out.

export const CONSENT_COOKIE = 'locusly-consent';
export const CONSENT_VERSION = '1';
export const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year (CNPD guidance)

export type ConsentValue = 'accepted' | 'essential';

// Cookie format: `${version}:${value}` so we can invalidate on schema changes.
export function parseConsentCookie(raw: string | undefined): ConsentValue | null {
  if (!raw) return null;
  const [version, value] = raw.split(':');
  if (version !== CONSENT_VERSION) return null;
  if (value === 'accepted' || value === 'essential') return value;
  return null;
}

export function readConsentClient(): ConsentValue | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]+)`),
  );
  return parseConsentCookie(match?.[1]);
}

export function writeConsentClient(value: ConsentValue, cookieDomain?: string): void {
  if (typeof document === 'undefined') return;
  const domainAttr = cookieDomain ? ` domain=${cookieDomain};` : '';
  document.cookie =
    `${CONSENT_COOKIE}=${CONSENT_VERSION}:${value};` +
    ` path=/;${domainAttr} max-age=${CONSENT_COOKIE_MAX_AGE}; samesite=lax`;
}

export function clearConsentClient(cookieDomain?: string): void {
  if (typeof document === 'undefined') return;
  const domainAttr = cookieDomain ? ` domain=${cookieDomain};` : '';
  document.cookie = `${CONSENT_COOKIE}=; path=/;${domainAttr} max-age=0; samesite=lax`;
}

// True when the user has opted in to non-essential cookies. Use this to gate
// analytics initialization, marketing pixels, etc.
//
//   if (hasAnalyticsConsent()) { initAnalytics(); }
export function hasAnalyticsConsent(): boolean {
  return readConsentClient() === 'accepted';
}
